// Key builder utilities for cache keys used by AsyncCacheManager axios wrappers.

/**
 * Fast, non-cryptographic 53-bit hash for strings.
 * Based on cyrb53 by Nick Cardillo (public domain style); adequate to shorten keys.
 */
export function hash53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const h = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return h.toString(36);
}

export interface KeyBuildOptions {
  /** When true, hash long param/body segments to keep keys compact. */
  hashLargeSegments?: boolean;
  /** Length threshold beyond which segments will be hashed. Default 256. */
  hashThreshold?: number;
}

function maybeHash(segment: string, opts?: KeyBuildOptions): string {
  const threshold = opts?.hashThreshold ?? 256;
  if (opts?.hashLargeSegments && segment.length > threshold) {
    return `#${hash53(segment)}`;
  }
  return segment;
}

/**
 * Metadata about a key segment before/after hashing.
 */
export interface SegmentMeta {
  /** Segment as used in the final key (maybe hashed) */
  used: string;
  /** Original segment prior to hashing (for search/dehash UIs) */
  original: string;
  /** Whether hashing was applied */
  hashed: boolean;
  /** Hash token (without prefix) if hashed */
  hash?: string;
}

/**
 * Result of building a cache key along with optional meta to aid dehashing.
 */
export interface KeyBuildResult {
  /** The final cache key string */
  key: string;
  /** Base URL context (if available) */
  base?: string;
  /** Path portion of the URL */
  path: string;
  /** HTTP method for body-based keys */
  method?: 'GET' | 'POST' | 'PUT';
  /** Params segment meta */
  params?: SegmentMeta;
  /** Body segment meta (for POST/PUT) */
  body?: SegmentMeta;
}

export function stableStringify(obj: any): string {
  try {
    if (obj == null || typeof obj !== 'object') return String(obj);
    const allKeys: string[] = [];
    JSON.stringify(obj, (key, value) => { allKeys.push(key); return value; });
    allKeys.sort();
    return JSON.stringify(obj, allKeys);
  } catch { return ''; }
}

export function buildCacheKeyForGet(
  baseURL: string | undefined,
  url: string,
  configParams?: Record<string, any>,
  options?: KeyBuildOptions
): string {
  let parsed: URL | null = null;
  try {
    parsed = new URL(url, baseURL || 'http://cache.local');
  } catch { /* fall back to raw */ }

  const urlParamsObj: Record<string, string> = {};
  if (parsed && parsed.searchParams) {
    parsed.searchParams.forEach((v, k) => { urlParamsObj[k] = v; });
  }
  const mergedParams = { ...urlParamsObj, ...(configParams || {}) };
  const path = parsed ? parsed.pathname : url;
  const baseTag = baseURL ? `BASE:${baseURL}|` : '';
  const paramsTag = maybeHash(stableStringify(mergedParams), options);
  return `GET:${baseTag}${path}?${paramsTag}`;
}

/**
 * Detailed variant that returns key + meta for dehash/search UIs.
 */
export function buildCacheKeyForGetDetailed(
  baseURL: string | undefined,
  url: string,
  configParams?: Record<string, any>,
  options?: KeyBuildOptions
): KeyBuildResult {
  let parsed: URL | null = null;
  try {
    parsed = new URL(url, baseURL || 'http://cache.local');
  } catch { /* fall back to raw */ }

  const urlParamsObj: Record<string, string> = {};
  if (parsed && parsed.searchParams) {
    parsed.searchParams.forEach((v, k) => { urlParamsObj[k] = v; });
  }
  const mergedParams = { ...urlParamsObj, ...(configParams || {}) };
  const path = parsed ? parsed.pathname : url;
  const baseTag = baseURL ? `BASE:${baseURL}|` : '';
  const original = stableStringify(mergedParams);
  const used = maybeHash(original, options);
  const key = `GET:${baseTag}${path}?${used}`;
  const hashed = used !== original;
  const hash = hashed && used.startsWith('#') ? used.slice(1) : undefined;
  return {
    key,
    base: baseURL,
    path,
    method: 'GET',
    params: { used, original, hashed, hash },
  };
}

export function buildCacheKeyForBody(
  method: 'POST' | 'PUT',
  baseURL: string | undefined,
  url: string,
  data?: any,
  configParams?: Record<string, any>,
  options?: KeyBuildOptions
): string {
  let parsed: URL | null = null;
  try {
    parsed = new URL(url, baseURL || 'http://cache.local');
  } catch { /* fall back to raw */ }

  const urlParamsObj: Record<string, string> = {};
  if (parsed && parsed.searchParams) {
    parsed.searchParams.forEach((v, k) => { urlParamsObj[k] = v; });
  }
  const mergedParams = { ...urlParamsObj, ...(configParams || {}) };
  const bodyTag = maybeHash(stableStringify(data), options);
  const path = parsed ? parsed.pathname : url;
  const baseTag = baseURL ? `BASE:${baseURL}|` : '';
  const paramsTag = maybeHash(stableStringify(mergedParams), options);
  return `${method}:${baseTag}${path}?${paramsTag}|BODY:${bodyTag}`;
}

/**
 * Detailed variant that returns key + meta (params/body) for dehash/search UIs.
 */
export function buildCacheKeyForBodyDetailed(
  method: 'POST' | 'PUT',
  baseURL: string | undefined,
  url: string,
  data?: any,
  configParams?: Record<string, any>,
  options?: KeyBuildOptions
): KeyBuildResult {
  let parsed: URL | null = null;
  try {
    parsed = new URL(url, baseURL || 'http://cache.local');
  } catch { /* fall back to raw */ }

  const urlParamsObj: Record<string, string> = {};
  if (parsed && parsed.searchParams) {
    parsed.searchParams.forEach((v, k) => { urlParamsObj[k] = v; });
  }
  const mergedParams = { ...urlParamsObj, ...(configParams || {}) };
  const path = parsed ? parsed.pathname : url;
  const baseTag = baseURL ? `BASE:${baseURL}|` : '';

  const originalParams = stableStringify(mergedParams);
  const usedParams = maybeHash(originalParams, options);
  const paramsHashed = usedParams !== originalParams;
  const paramsHash = paramsHashed && usedParams.startsWith('#') ? usedParams.slice(1) : undefined;

  const originalBody = stableStringify(data);
  const usedBody = maybeHash(originalBody, options);
  const bodyHashed = usedBody !== originalBody;
  const bodyHash = bodyHashed && usedBody.startsWith('#') ? usedBody.slice(1) : undefined;

  const key = `${method}:${baseTag}${path}?${usedParams}|BODY:${usedBody}`;
  return {
    key,
    base: baseURL,
    path,
    method,
    params: { used: usedParams, original: originalParams, hashed: paramsHashed, hash: paramsHash },
    body: { used: usedBody, original: originalBody, hashed: bodyHashed, hash: bodyHash },
  };
}
